import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  FONTS,
  COLORS,
  BREAKPOINTS,
  SHADOWS,
} from "../../utils/styleConstants";
import useNetworkStatus from "../../hooks/useNetworkStatus";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  flex: 1;
`;

const Title = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin-bottom: 0.5rem;
`;

const Description = styled.p`
  color: ${COLORS.ACCENT};
  font-size: 1.1rem;
  font-family: ${FONTS.PRIMARY};
  margin-bottom: 2rem;
  line-height: 1.5;
  max-width: 80%;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    max-width: 100%;
  }
`;

// Nuevo contenedor para las secciones horizontales
const HorizontalSections = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.5rem;
  padding-left: ${(props) => (props.isFirst ? "0" : "1.5rem")};
  border-radius: 8px;
  background-color: white;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 600;
  min-height: 2.5em;
  display: flex;
  align-items: center;
`;

const SectionDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  font-family: ${FONTS.SECONDARY};
  min-height: 3em;
`;

const HorizontalField = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 1rem;
  gap: 1rem;

  &:focus-within {
    border-bottom-color: ${COLORS.PRIMARY};
  }

  &.last-field {
    margin-bottom: 0.5rem;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 2rem;
    border-bottom: 1px solid #bad1e6;
  }
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  display: flex;
  align-items: center;
  min-width: 40%;
  white-space: nowrap;

  &:after {
    content: ${(props) => (props.required ? '"*"' : '""')};
    color: ${COLORS.SECONDARY};
    margin-left: 4px;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    width: 100%;
    white-space: normal;
  }
`;

const Input = styled.input`
  padding: 0.25rem 0;
  border: none;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;

  &:focus {
    outline: none;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 0.9rem;
    text-align: left;
    width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 2rem;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ButtonWithIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StepIndicator = styled.div`
  text-align: center;
  color: #ff7057;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  font-family: ${FONTS.SECONDARY};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 120px; /* Ancho un poco mayor para asegurar espacio suficiente */
  margin-left: auto;
  white-space: nowrap;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: white;
  color: #a8b9d5;
  border: 1px solid #dbe4f2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: #f5f8fd;
  }

  &:disabled {
    color: #c4c4c4;
    border-color: #e2e2e2;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const SaveButton = styled(Button)`
  background-color: white;
  color: #ff7057;
  border: 1px solid #ff7057;
  box-shadow: 0 1px 2px rgba(255, 112, 87, 0.1);

  &:hover {
    background-color: #fff8f6;
  }

  &:disabled {
    color: #ffb8a8;
    border-color: #ffb8a8;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: #ff7057;
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(255, 112, 87, 0.2);

  &:hover {
    background-color: #ff5b40;
  }

  &:disabled {
    background-color: #ffc1ad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1rem;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 22px;
  margin-right: 0;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #4d7af7;
  }

  &:checked + span:before {
    transform: translateX(22px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e2e2e2;
  transition: 0.3s;
  border-radius: 34px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const RequiredMessage = styled.p`
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.4rem;
  font-family: ${FONTS.SECONDARY};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled.div`
  color: ${COLORS.ERROR};
  font-size: 0.75rem;
  position: absolute;
  bottom: 0.3rem;
  right: 1rem;
  margin: 0;
  font-family: ${FONTS.SECONDARY};

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    bottom: -1.5rem;
    right: 0rem;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const FormWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

// Estilos para el sistema de validación de documentos
const DocumentValidationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const DocumentTypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`;

const TypeDropdownContainer = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
`;

const Select = styled.select`
  padding: 0.25rem 0;
  border: none;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23a8b9d5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1rem;
  padding-right: 2rem;

  &:focus {
    outline: none;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 0.9rem;
    text-align: left;
    width: 100%;
  }
`;

const InfoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.5rem;
  cursor: pointer;
  color: ${COLORS.ACCENT};
  outline: none;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InfoIconSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
      fill="currentColor"
    />
  </svg>
);

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const Tooltip = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: ${props => props.$isContraparte || props.$isRepresentacion ? 'auto' : '0'};
  right: ${props => props.$isContraparte || props.$isRepresentacion ? '0' : '0'};
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem;
  font-family: ${FONTS.SECONDARY};
  font-size: 0.8rem;
  line-height: 1.4;
  color: #495057;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: opacity 0.3s, visibility 0.3s;
  max-width: 500px;
  min-width: 350px;

  &::before {
    content: "";
    position: absolute;
    top: -8px;
    right: ${props => props.$isContraparte || props.$isRepresentacion ? 'auto' : '1rem'};
    left: ${props => props.$isContraparte || props.$isRepresentacion ? '1rem' : 'auto'};
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #f8f9fa;
  }

  &::after {
    content: "";
    position: absolute;
    top: -7px;
    right: ${props => props.$isContraparte || props.$isRepresentacion ? 'auto' : '1rem'};
    left: ${props => props.$isContraparte || props.$isRepresentacion ? '1rem' : 'auto'};
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #e9ecef;
  }
`;

const DocumentErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.8rem;
  font-family: ${FONTS.SECONDARY};
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ErrorIconSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const OfertaStep1 = ({
  formData,
  updateFormData,
  nextStep,
  saveDraft,
  isSubmitting,
  isSavingDraft,
  isReadOnly = false,
  hasShownNetworkModal = false,
  isOnline = true,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Estados para el formulario
  const [ofertante, setOfertante] = useState(formData?.ofertante || {
    nombre: "",
    apellidos: "",
    email: "",
    identificacion: "",
    movil: "",
  });

  const [contraparte, setContraparte] = useState(formData?.contraparte || {
    nombre: "",
    apellidos: "",
    email: "",
    identificacion: "",
    movil: "",
  });

  const [representacion, setRepresentacion] = useState(
    formData?.representacionContraparte || {
      activa: false,
      nombre: "",
      apellidos: "",
      email: "",
      identificacion: "",
      movil: "",
    }
  );

  // Estados para validación
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [allowAutoFill, setAllowAutoFill] = useState(false);

  // Estados para el sistema de validación de documentos
  const [documentTypes, setDocumentTypes] = useState({
    ofertante: "",
    contraparte: "",
    representacion: "",
  });

  const [showTooltips, setShowTooltips] = useState({
    ofertante: false,
    contraparte: false,
    representacion: false,
  });

  // Expresiones regulares para validación de documentos
  const documentRegex = {
    CIF: /^[A-Z][0-9]{7}[A-Z0-9]$/,
    NIF: /^[0-9]{8}[A-Z]$/,
    NIE: /^[XYZ][0-9]{7}[A-Z]$/,
    Passport: /^[A-Z]{2,3}[0-9]{6}$/,
  };

  // Función para validar formato de documento
  const validateDocumentFormat = (type, value) => {
    if (!type || !value) return null;

    // Mapear opciones visuales a claves de validación
    const normalizedType =
      type === "DNI" ? "NIF" : type === "NIF/CIF" ? ( /^[A-Z]/.test(value) ? "CIF" : "NIF" ) : type;

    const regex = documentRegex[normalizedType];
    if (!regex) return "Tipo de documento no válido";

    // Para NIF/DNI, validación más estricta
    if (normalizedType === "NIF") {
      if (!value.trim()) return null;
      if (!/^[0-9A-Z]+$/.test(value)) {
        return "El DNI/NIF solo puede contener números y letras";
      }
      if (value.length < 8) {
        return "El DNI/NIF debe tener 8 números seguidos de 1 letra";
      }
      if (value.length === 8) {
        if (!/^[0-9]{8}$/.test(value)) {
          return "Los primeros 8 caracteres del DNI/NIF deben ser números";
        }
        return "Falta la letra final del DNI/NIF";
      }
      if (value.length === 9) {
        return regex.test(value) ? null : "Formato de DNI/NIF no válido";
      }
      return "El DNI/NIF no puede tener más de 9 caracteres";
    }

    return regex.test(value) ? null : `Formato de ${normalizedType} no válido`;
  };

  // Función para obtener el mensaje de ayuda según el tipo de documento
  const getDocumentHelpMessage = (type) => {
    const normalizedType = type === "DNI" ? "NIF" : type === "NIF/CIF" ? "NIF/CIF" : type;
    switch (normalizedType) {
      case "CIF":
        return "Formato: 1 letra + 7 números + 1 letra o número. Ej: A1234567B";
      case "NIF":
        return "Formato: 8 números + 1 letra. Ej: 12345678A";
      case "NIF/CIF":
        return "Introduce el número y seleccionaremos NIF o CIF automáticamente";
      case "NIE":
        return "Formato: X/Y/Z + 7 números + 1 letra. Ej: X1234567A";
      case "Passport":
        return "Formato: 2-3 letras + 6 números. Ej: AB123456";
      default:
        return "Selecciona un tipo de documento para ver el formato requerido";
    }
  };

  // Función para manejar cambios en el tipo de documento
  const handleDocumentTypeChange = (section, type) => {
    setDocumentTypes(prev => ({
      ...prev,
      [section]: type
    }));
    
    // Obtener el valor actual del campo de identificación
    const currentValue = section === "ofertante" ? ofertante.identificacion :
                        section === "contraparte" ? contraparte.identificacion :
                        representacion.identificacion;

    // Validar inmediatamente el valor actual contra el nuevo tipo
    const errorKey = `${section}Identificacion`;
    if (currentValue && type) {
      const validationError = validateDocumentFormat(type, currentValue);
      if (validationError) {
        setErrors(prev => ({ ...prev, [errorKey]: validationError }));
        // Marcar como tocado para mostrar el error inmediatamente
        setTouched(prev => ({ ...prev, [errorKey]: true }));
      } else {
        // Limpiar error si el formato es válido
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }
    } else if (type) {
      // Si hay un tipo seleccionado pero no hay valor, limpiar errores de tipo no seleccionado
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[errorKey] && newErrors[errorKey].includes('Debe seleccionar un tipo de documento')) {
          delete newErrors[errorKey];
        }
        return newErrors;
      });
    }

    // Validación inmediata con valores actuales para actualizar el botón
    const validateFormImmediately = () => {
      const newErrors = {};
      
      // Validar campos de ofertante
      if (!ofertante.nombre) newErrors.ofertanteNombre = "El nombre es obligatorio";
      if (!ofertante.apellidos) newErrors.ofertanteApellidos = "Los apellidos son obligatorios";
      if (!ofertante.email) {
        newErrors.ofertanteEmail = "El email es obligatorio";
      } else if (!/\S+@\S+\.\S+/.test(ofertante.email)) {
        newErrors.ofertanteEmail = "El formato del email no es válido";
      }
      
      // Validar documento de identidad del ofertante con el tipo actual
      const currentOfertanteType = section === "ofertante" ? type : documentTypes.ofertante;
      if (!currentOfertanteType) {
        newErrors.ofertanteIdentificacion = "Debe seleccionar un tipo de documento";
      } else if (!ofertante.identificacion) {
        newErrors.ofertanteIdentificacion = "El número de documento es obligatorio";
      } else {
        const validationError = validateDocumentFormat(currentOfertanteType, ofertante.identificacion);
        if (validationError) {
          newErrors.ofertanteIdentificacion = validationError;
        }
      }

      // Validar campos de contraparte
      if (!contraparte.nombre) newErrors.contraparteNombre = "El nombre es obligatorio";
      if (!contraparte.apellidos) newErrors.contraparteApellidos = "Los apellidos son obligatorios";
      if (!contraparte.email) {
        newErrors.contraparteEmail = "El email es obligatorio";
      } else if (!/\S+@\S+\.\S+/.test(contraparte.email)) {
        newErrors.contraparteEmail = "El formato del email no es válido";
      }
      
      // Validar documento de identidad de la contraparte (opcional)
      const currentContraparteType = section === "contraparte" ? type : documentTypes.contraparte;
      if (currentContraparteType && contraparte.identificacion) {
        const validationError = validateDocumentFormat(currentContraparteType, contraparte.identificacion);
        if (validationError) {
          newErrors.contraparteIdentificacion = validationError;
        }
      } else if (currentContraparteType && !contraparte.identificacion) {
        // Si se selecciona tipo de documento, el número es obligatorio
        newErrors.contraparteIdentificacion = "El número de documento es obligatorio cuando se selecciona un tipo";
      } else if (!currentContraparteType && contraparte.identificacion) {
        // Si se ingresa número pero no se selecciona tipo
        newErrors.contraparteIdentificacion = "Debe seleccionar un tipo de documento";
      }

      // Validar campos de representación si está activa
      if (representacion.activa) {
        if (!representacion.nombre) newErrors.representacionNombre = "El nombre es obligatorio";
        if (!representacion.apellidos) newErrors.representacionApellidos = "Los apellidos son obligatorios";
        if (!representacion.email) {
          newErrors.representacionEmail = "El email es obligatorio";
        } else if (!/\S+@\S+\.\S+/.test(representacion.email)) {
          newErrors.representacionEmail = "El formato del email no es válido";
        }
        
        // Validar documento de identidad de la representación (opcional)
        const currentRepresentacionType = section === "representacion" ? type : documentTypes.representacion;
        if (currentRepresentacionType && representacion.identificacion) {
          const validationError = validateDocumentFormat(currentRepresentacionType, representacion.identificacion);
          if (validationError) {
            newErrors.representacionIdentificacion = validationError;
          }
        } else if (currentRepresentacionType && !representacion.identificacion) {
          // Si se selecciona tipo de documento, el número es obligatorio
          newErrors.representacionIdentificacion = "El número de documento es obligatorio cuando se selecciona un tipo";
        } else if (!currentRepresentacionType && representacion.identificacion) {
          // Si se ingresa número pero no se selecciona tipo
          newErrors.representacionIdentificacion = "Debe seleccionar un tipo de documento";
        }
      }

      // Actualizar errores y validación del formulario
      setErrors(newErrors);
      setIsFormValid(Object.keys(newErrors).length === 0);
    };

    // Ejecutar validación inmediata
    validateFormImmediately();
  };

  // Función helper para capturar identificación de manera robusta
  const captureIdentificationValue = (section, currentStateValue) => {
    const sectionCapitalized = section.charAt(0).toUpperCase() + section.slice(1);
    const element = document.getElementById(`${section}Identificacion`);
    
    // Priorizar valor del DOM, luego del estado
    if (element && element.value) {
      return element.value;
    } else if (currentStateValue) {
      return currentStateValue;
    }
    
    return "";
  };

  // Función completa para capturar todos los valores de una sección de manera robusta
  const captureAllSectionValues = (section, currentState) => {
    const updated = { ...currentState };
    
    // Capturar campos normales
    ["nombre", "apellidos", "email", "movil"].forEach((campo) => {
      const inputElement = document.getElementById(`${section}${campo}`);
      if (inputElement && inputElement.value) {
        updated[campo] = inputElement.value;
      }
    });
    
    // Capturar identificación de manera robusta
    updated.identificacion = captureIdentificationValue(section, currentState.identificacion);
    
    return updated;
  };

  // Función para manejar cambios en el número de documento
  const handleDocumentNumberChange = (section, value) => {
    const type = documentTypes[section];
    const validationError = validateDocumentFormat(type, value);

    // Capturar valores actuales del DOM para otros campos antes de actualizar
    const currentOfertante = captureAllSectionValues("ofertante", ofertante);
    const currentContraparte = captureAllSectionValues("contraparte", contraparte);
    const currentRepresentacion = representacion.activa 
      ? captureAllSectionValues("representacion", representacion)
      : representacion;

    // Actualizar el estado correspondiente con el nuevo valor
    if (section === "ofertante") {
      const updated = { ...currentOfertante, identificacion: value };
      setOfertante(updated);
      setContraparte(currentContraparte);
      setRepresentacion(currentRepresentacion);
      // Actualizar formData con todos los valores actuales
      updateFormData("ofertante", updated);
      updateFormData("contraparte", currentContraparte);
      updateFormData("representacionContraparte", currentRepresentacion);
    } else if (section === "contraparte") {
      const updated = { ...currentContraparte, identificacion: value };
      setOfertante(currentOfertante);
      setContraparte(updated);
      setRepresentacion(currentRepresentacion);
      // Actualizar formData con todos los valores actuales
      updateFormData("ofertante", currentOfertante);
      updateFormData("contraparte", updated);
      updateFormData("representacionContraparte", currentRepresentacion);
    } else if (section === "representacion") {
      const updated = { ...currentRepresentacion, identificacion: value };
      setOfertante(currentOfertante);
      setContraparte(currentContraparte);
      setRepresentacion(updated);
      // Actualizar formData con todos los valores actuales
      updateFormData("ofertante", currentOfertante);
      updateFormData("contraparte", currentContraparte);
      updateFormData("representacionContraparte", updated);
    }

    // Actualizar errores
    const errorKey = `${section}Identificacion`;
    if (validationError) {
      setErrors(prev => ({ ...prev, [errorKey]: validationError }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Create refs at the top level of the component
  const prevValues = useRef({
    ofertante: {},
    contraparte: {},
    representacion: {},
  });

  const currentValues = useRef({
    ofertante: {},
    contraparte: {},
    representacion: {},
  });

  // Update state when formData changes (for example when editing an existing offer)
  useEffect(() => {
    console.log("OfertaStep1 received formData:", formData);
    
    // Solo actualizar el estado si los valores son diferentes Y si el usuario no está escribiendo
    // Preservar valores actuales del DOM antes de actualizar
    const preserveCurrentValues = (currentState, newData, section) => {
      const currentDomValue = captureIdentificationValue(section, currentState.identificacion);
      
      // Si hay un valor en el DOM y es diferente del nuevo, preservarlo
      if (currentDomValue && currentDomValue !== newData.identificacion) {
        return {
          ...newData,
          identificacion: currentDomValue
        };
      }
      
      return newData;
    };

    // Actualizar estados preservando valores del DOM cuando sea necesario
    setOfertante(prev => preserveCurrentValues(prev, formData.ofertante, "ofertante"));
    setContraparte(prev => preserveCurrentValues(prev, formData.contraparte, "contraparte"));
    setRepresentacion(prev => preserveCurrentValues(prev, formData.representacionContraparte, "representacion"));

    // Si hay datos en formData, significa que estamos volviendo del paso 2
    // o editando una oferta existente, por lo que debemos marcar como interactuado
    if (formData.ofertante?.nombre || formData.contraparte?.nombre) {
      setHasInteracted(true);
      validateForm();
    }

    // Allow autofill detection after data has been loaded and settled
    const allowAutoFillTimer = setTimeout(() => {
      setAllowAutoFill(true);
    }, 3000);

    return () => clearTimeout(allowAutoFillTimer);
  }, [formData]);

  // Deshabilitar completamente la validación inicial
  useEffect(() => {
    // Deshabilitar validación hasta que haya interacción real
    const validationTimer = setTimeout(() => {
      if (hasInteracted) {
        validateForm();
      }
    }, 300);

    return () => clearTimeout(validationTimer);
  }, [ofertante, contraparte, representacion, hasInteracted]);

  // Cargar datos del usuario sin activar validación
  useEffect(() => {
    if (user && !hasInteracted && !formData.ofertante.nombre) {
      // Only auto-fill if form is empty (not editing an existing offer)
      setOfertante((prev) => ({
        ...prev,
        nombre: user.nombre || prev.nombre,
        apellidos: user.apellidos || prev.apellidos,
        identificacion: user.identificacion || prev.identificacion,
      }));
    }
  }, [user, formData.ofertante.nombre, hasInteracted]);

  // Marcar interacción cuando el usuario hace algo
  const markInteracted = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
  };

  const validateForm = () => {
    // No validar si no ha habido interacción
    if (!hasInteracted) {
      setErrors({});
      return;
    }

    const newErrors = {};

    // Validar campos de ofertante
    if (!ofertante.nombre)
      newErrors.ofertanteNombre = "El nombre es obligatorio";
    if (!ofertante.apellidos)
      newErrors.ofertanteApellidos = "Los apellidos son obligatorios";
    if (!ofertante.email) {
      newErrors.ofertanteEmail = "El email es obligatorio";
    } else if (!/\S+@\S+\.\S+/.test(ofertante.email)) {
      newErrors.ofertanteEmail = "El formato del email no es válido";
    }
    
    // Validar documento de identidad del ofertante
    if (!documentTypes.ofertante) {
      newErrors.ofertanteIdentificacion = "Debe seleccionar un tipo de documento";
    } else if (!ofertante.identificacion) {
      newErrors.ofertanteIdentificacion = "El número de documento es obligatorio";
    } else {
      const validationError = validateDocumentFormat(documentTypes.ofertante, ofertante.identificacion);
      if (validationError) {
        newErrors.ofertanteIdentificacion = validationError;
      }
    }

    // Validar campos de contraparte
    if (!contraparte.nombre)
      newErrors.contraparteNombre = "El nombre es obligatorio";
    if (!contraparte.apellidos)
      newErrors.contraparteApellidos = "Los apellidos son obligatorios";
    if (!contraparte.email) {
      newErrors.contraparteEmail = "El email es obligatorio";
    } else if (!/\S+@\S+\.\S+/.test(contraparte.email)) {
      newErrors.contraparteEmail = "El formato del email no es válido";
    }
    
    // Validar documento de identidad de la contraparte (opcional)
    if (documentTypes.contraparte && contraparte.identificacion) {
      const validationError = validateDocumentFormat(documentTypes.contraparte, contraparte.identificacion);
      if (validationError) {
        newErrors.contraparteIdentificacion = validationError;
      }
    } else if (documentTypes.contraparte && !contraparte.identificacion) {
      // Si se selecciona tipo de documento, el número es obligatorio
      newErrors.contraparteIdentificacion = "El número de documento es obligatorio cuando se selecciona un tipo";
    } else if (!documentTypes.contraparte && contraparte.identificacion) {
      // Si se ingresa número pero no se selecciona tipo
      newErrors.contraparteIdentificacion = "Debe seleccionar un tipo de documento";
    }

    // Validar campos de representación si está activa
    if (representacion.activa) {
      if (!representacion.nombre)
        newErrors.representacionNombre = "El nombre es obligatorio";
      if (!representacion.apellidos)
        newErrors.representacionApellidos = "Los apellidos son obligatorios";
      if (!representacion.email) {
        newErrors.representacionEmail = "El email es obligatorio";
      } else if (!/\S+@\S+\.\S+/.test(representacion.email)) {
        newErrors.representacionEmail = "El formato del email no es válido";
      }
      
      // Validar documento de identidad de la representación (opcional)
      if (documentTypes.representacion && representacion.identificacion) {
        const validationError = validateDocumentFormat(documentTypes.representacion, representacion.identificacion);
        if (validationError) {
          newErrors.representacionIdentificacion = validationError;
        }
      } else if (documentTypes.representacion && !representacion.identificacion) {
        // Si se selecciona tipo de documento, el número es obligatorio
        newErrors.representacionIdentificacion = "El número de documento es obligatorio cuando se selecciona un tipo";
      } else if (!documentTypes.representacion && representacion.identificacion) {
        // Si se ingresa número pero no se selecciona tipo
        newErrors.representacionIdentificacion = "Debe seleccionar un tipo de documento";
      }
    }

    setErrors(newErrors);
    setIsFormValid(Object.keys(newErrors).length === 0);
  };

  // Actualizar manejadores de eventos para marcar interacción
  const handleBlur = (section, field) => {
    markInteracted();
    const errorKey = `${section}${
      field.charAt(0).toUpperCase() + field.slice(1)
    }`;
    setTouched((prev) => ({
      ...prev,
      [errorKey]: true,
    }));
  };

  const handleOfertanteChange = (e) => {
    markInteracted();
    const { name, value } = e.target;
    
    // Capturar valores actuales del DOM para otros campos antes de actualizar
    const currentOfertante = captureAllSectionValues("ofertante", ofertante);
    const currentContraparte = captureAllSectionValues("contraparte", contraparte);
    const currentRepresentacion = representacion.activa 
      ? captureAllSectionValues("representacion", representacion)
      : representacion;
    
    const updated = { ...currentOfertante, [name]: value };
    setOfertante(updated);

    // Actualizar también los otros estados con valores capturados del DOM
    setContraparte(currentContraparte);
    setRepresentacion(currentRepresentacion);

    // Update parent component state with current DOM values
    updateFormData("ofertante", updated);
    updateFormData("contraparte", currentContraparte);
    updateFormData("representacionContraparte", currentRepresentacion);
  };

  const handleContraparteChange = (e) => {
    markInteracted();
    const { name, value } = e.target;
    
    // Capturar valores actuales del DOM para otros campos antes de actualizar
    const currentOfertante = captureAllSectionValues("ofertante", ofertante);
    const currentContraparte = captureAllSectionValues("contraparte", contraparte);
    const currentRepresentacion = representacion.activa 
      ? captureAllSectionValues("representacion", representacion)
      : representacion;
    
    const updated = { ...currentContraparte, [name]: value };
    setContraparte(updated);

    // Actualizar también los otros estados con valores capturados del DOM
    setOfertante(currentOfertante);
    setRepresentacion(currentRepresentacion);

    // Update parent component state with current DOM values
    updateFormData("ofertante", currentOfertante);
    updateFormData("contraparte", updated);
    updateFormData("representacionContraparte", currentRepresentacion);
  };

  const handleRepresentacionChange = (e) => {
    markInteracted();
    const { name, value } = e.target;
    
    // Capturar valores actuales del DOM para otros campos antes de actualizar
    const currentOfertante = captureAllSectionValues("ofertante", ofertante);
    const currentContraparte = captureAllSectionValues("contraparte", contraparte);
    const currentRepresentacion = representacion.activa 
      ? captureAllSectionValues("representacion", representacion)
      : representacion;
    
    const updated = { ...currentRepresentacion, [name]: value };
    setRepresentacion(updated);

    // Actualizar también los otros estados con valores capturados del DOM
    setOfertante(currentOfertante);
    setContraparte(currentContraparte);

    // Update parent component state with current DOM values
    updateFormData("ofertante", currentOfertante);
    updateFormData("contraparte", currentContraparte);
    updateFormData("representacionContraparte", updated);
  };

  const toggleRepresentacion = () => {
    markInteracted();
    
    // Capturar valores actuales del DOM antes de hacer toggle
    const currentOfertante = captureAllSectionValues("ofertante", ofertante);
    const currentContraparte = captureAllSectionValues("contraparte", contraparte);
    const currentRepresentacion = representacion.activa 
      ? captureAllSectionValues("representacion", representacion)
      : representacion;
    
    const updated = {
      ...currentRepresentacion,
      activa: !representacion.activa,
    };
    setRepresentacion(updated);

    // Actualizar también los otros estados con valores capturados del DOM
    setOfertante(currentOfertante);
    setContraparte(currentContraparte);

    // Update parent component state with current DOM values
    updateFormData("ofertante", currentOfertante);
    updateFormData("contraparte", currentContraparte);
    updateFormData("representacionContraparte", updated);
  };

  // Función para renderizar campo de documento de identidad con validación
  const renderDocumentField = (section, label, required = true) => {
    const getValue = () => {
      if (section === "ofertante") return ofertante.identificacion;
      if (section === "contraparte") return contraparte.identificacion;
      if (section === "representacion") return representacion.identificacion;
      return "";
    };

    const getDocumentType = () => {
      return documentTypes[section] || "";
    };

    const getError = () => {
      if (!hasInteracted) return null;
      const errorKey = `${section}Identificacion`;
      return errors[errorKey];
    };

    const isTouched = () => {
      const errorKey = `${section}Identificacion`;
      return touched[errorKey];
    };

    const currentValue = getValue();
    const currentType = getDocumentType();
    
    console.log(`🎨 renderDocumentField - ${section}:`, {
      value: currentValue,
      type: currentType,
      hasInteracted,
      error: getError(),
      touched: isTouched()
    });

    return (
      <HorizontalField>
        <Label htmlFor={`${section}Identificacion`} required={required}>
          {label}
        </Label>
        <DocumentValidationContainer>
          <DocumentTypeRow>
            <TypeDropdownContainer>
              <Select
                id={`${section}DocumentType`}
                value={currentType}
                onChange={(e) => handleDocumentTypeChange(section, e.target.value)}
                disabled={isReadOnly}
              >
                <option value="">Tipo de documento</option>
                {section !== "representacion" ? (
                  <>
                    <option value="DNI">DNI</option>
                    <option value="NIE">NIE</option>
                    <option value="NIF/CIF">NIF / CIF</option>
                    <option value="Passport">Pasaporte</option>
                  </>
                ) : (
                  <>
                    <option value="DNI">DNI</option>
                    <option value="NIE">NIE</option>
                  </>
                )}
              </Select>
            </TypeDropdownContainer>
            <TooltipContainer>
              <InfoIcon
                onMouseEnter={() => setShowTooltips(prev => ({ ...prev, [section]: true }))}
                onMouseLeave={() => setShowTooltips(prev => ({ ...prev, [section]: false }))}
                onFocus={() => setShowTooltips(prev => ({ ...prev, [section]: true }))}
                onBlur={() => setShowTooltips(prev => ({ ...prev, [section]: false }))}
              >
                <InfoIconSvg />
              </InfoIcon>
              <Tooltip visible={showTooltips[section]} $isContraparte={section === "contraparte"} $isRepresentacion={section === "representacion"}>
                {getDocumentHelpMessage(currentType)}
              </Tooltip>
            </TooltipContainer>
          </DocumentTypeRow>
          <Input
            type="text"
            id={`${section}Identificacion`}
            value={currentValue}
            onChange={(e) => handleDocumentNumberChange(section, e.target.value)}
            onBlur={() => handleBlur(section, "identificacion")}
            onClick={markInteracted}
            readOnly={isReadOnly}
            placeholder={currentType ? `Introduce tu ${currentType === 'DNI' ? 'DNI/NIF' : currentType}` : "Selecciona tipo de documento"}
          />
          {hasInteracted && isTouched() && getError() && (
            <DocumentErrorMessage>
              <ErrorIconSvg />
              {getError()}
            </DocumentErrorMessage>
          )}
        </DocumentValidationContainer>
      </HorizontalField>
    );
  };

  // Actualizar renderField para no mostrar errores hasta que haya interacción
  const renderField = (
    section,
    field,
    label,
    type = "text",
    required = true,
    isLast = false
  ) => {
    const getValue = () => {
      if (section === "ofertante") return ofertante[field];
      if (section === "contraparte") return contraparte[field];
      if (section === "representacion") return representacion[field];
      return "";
    };

    const getHandleChange = () => {
      if (section === "ofertante") return handleOfertanteChange;
      if (section === "contraparte") return handleContraparteChange;
      if (section === "representacion") return handleRepresentacionChange;
      return () => {};
    };

    const getError = () => {
      if (!hasInteracted) return null;
      const errorKey = `${section}${
        field.charAt(0).toUpperCase() + field.slice(1)
      }`;
      return errors[errorKey];
    };

    const isTouched = () => {
      const errorKey = `${section}${
        field.charAt(0).toUpperCase() + field.slice(1)
      }`;
      return touched[errorKey];
    };

    return (
      <HorizontalField className={isLast ? "last-field" : ""}>
        <Label htmlFor={`${section}${field}`} required={required}>
          {label}
        </Label>
        <Input
          type={type}
          id={`${section}${field}`}
          name={field}
          value={getValue()}
          onChange={getHandleChange()}
          onBlur={() => handleBlur(section, field)}
          onClick={markInteracted}
          readOnly={isReadOnly}
        />
        {hasInteracted && isTouched() && getError() && (
          <DocumentErrorMessage>{getError()}</DocumentErrorMessage>
        )}
      </HorizontalField>
    );
  };

  // Actualizar botón de continuar
  const handleContinue = () => {
    markInteracted();

    // Marcar todos los campos como tocados
    const fieldsToTouch = {
      ofertanteNombre: true,
      ofertanteApellidos: true,
      ofertanteEmail: true,
      ofertanteIdentificacion: true,
      contraparteNombre: true,
      contraparteApellidos: true,
      contraparteEmail: true,
      contraparteIdentificacion: true,
    };

    if (representacion.activa) {
      fieldsToTouch.representacionNombre = true;
      fieldsToTouch.representacionApellidos = true;
      fieldsToTouch.representacionEmail = true;
      fieldsToTouch.representacionIdentificacion = true;
    }

    setTouched(fieldsToTouch);
    validateForm();

    // Verificar inmediatamente si el formulario es válido
    const newErrors = {};

    // Validar campos de ofertante
    if (!ofertante.nombre)
      newErrors.ofertanteNombre = "El nombre es obligatorio";
    if (!ofertante.apellidos)
      newErrors.ofertanteApellidos = "Los apellidos son obligatorios";
    if (!ofertante.email) {
      newErrors.ofertanteEmail = "El email es obligatorio";
    } else if (!/\S+@\S+\.\S+/.test(ofertante.email)) {
      newErrors.ofertanteEmail = "El formato del email no es válido";
    }
    
    // Validar documento de identidad del ofertante
    if (!documentTypes.ofertante) {
      newErrors.ofertanteIdentificacion = "Debe seleccionar un tipo de documento";
    } else if (!ofertante.identificacion) {
      newErrors.ofertanteIdentificacion = "El número de documento es obligatorio";
    } else {
      const validationError = validateDocumentFormat(documentTypes.ofertante, ofertante.identificacion);
      if (validationError) {
        newErrors.ofertanteIdentificacion = validationError;
      }
    }

    // Validar campos de contraparte
    if (!contraparte.nombre)
      newErrors.contraparteNombre = "El nombre es obligatorio";
    if (!contraparte.apellidos)
      newErrors.contraparteApellidos = "Los apellidos son obligatorios";
    if (!contraparte.email) {
      newErrors.contraparteEmail = "El email es obligatorio";
    } else if (!/\S+@\S+\.\S+/.test(contraparte.email)) {
      newErrors.contraparteEmail = "El formato del email no es válido";
    }
    
    // Validar documento de identidad de la contraparte (opcional)
    if (documentTypes.contraparte && contraparte.identificacion) {
      const validationError = validateDocumentFormat(documentTypes.contraparte, contraparte.identificacion);
      if (validationError) {
        newErrors.contraparteIdentificacion = validationError;
      }
    } else if (documentTypes.contraparte && !contraparte.identificacion) {
      // Si se selecciona tipo de documento, el número es obligatorio
      newErrors.contraparteIdentificacion = "El número de documento es obligatorio cuando se selecciona un tipo";
    } else if (!documentTypes.contraparte && contraparte.identificacion) {
      // Si se ingresa número pero no se selecciona tipo
      newErrors.contraparteIdentificacion = "Debe seleccionar un tipo de documento";
    }

    // Validar campos de representación si está activa
    if (representacion.activa) {
      if (!representacion.nombre)
        newErrors.representacionNombre = "El nombre es obligatorio";
      if (!representacion.apellidos)
        newErrors.representacionApellidos = "Los apellidos son obligatorios";
      if (!representacion.email) {
        newErrors.representacionEmail = "El email es obligatorio";
      } else if (!/\S+@\S+\.\S+/.test(representacion.email)) {
        newErrors.representacionEmail = "El formato del email no es válido";
      }
      
      // Validar documento de identidad de la representación (opcional)
      if (documentTypes.representacion && representacion.identificacion) {
        const validationError = validateDocumentFormat(documentTypes.representacion, representacion.identificacion);
        if (validationError) {
          newErrors.representacionIdentificacion = validationError;
        }
      } else if (documentTypes.representacion && !representacion.identificacion) {
        // Si se selecciona tipo de documento, el número es obligatorio
        newErrors.representacionIdentificacion = "El número de documento es obligatorio cuando se selecciona un tipo";
      } else if (!documentTypes.representacion && representacion.identificacion) {
        // Si se ingresa número pero no se selecciona tipo
        newErrors.representacionIdentificacion = "Debe seleccionar un tipo de documento";
      }
    }

    const formValid = Object.keys(newErrors).length === 0;

    if (formValid) {
      // Capturar valores actuales directamente del DOM antes de avanzar
      // Similar a handleSaveDraft pero capturando para nextStep
      const capturarValoresReales = () => {
        // Ofertante - usar función robusta para capturar valores
        const ofertanteActualizado = captureAllSectionValues("ofertante", ofertante);

        // Capturar tipo de documento del ofertante
        const ofertanteTypeElement = document.getElementById("ofertanteDocumentType");
        if (ofertanteTypeElement && ofertanteTypeElement.value) {
          setDocumentTypes(prev => ({ ...prev, ofertante: ofertanteTypeElement.value }));
        }

        // Contraparte - usar función robusta para capturar valores
        const contraparteActualizada = captureAllSectionValues("contraparte", contraparte);

        // Capturar tipo de documento de la contraparte
        const contraparteTypeElement = document.getElementById("contraparteDocumentType");
        if (contraparteTypeElement && contraparteTypeElement.value) {
          setDocumentTypes(prev => ({ ...prev, contraparte: contraparteTypeElement.value }));
        }

        // Representación - usar función robusta para capturar valores
        const representacionActualizada = { ...representacion };
        
        if (representacion.activa) {
          const representacionCaptured = captureAllSectionValues("representacion", representacion);
          Object.assign(representacionActualizada, representacionCaptured);

          // Capturar tipo de documento de la representación
          const representacionTypeElement = document.getElementById("representacionDocumentType");
          if (representacionTypeElement && representacionTypeElement.value) {
            setDocumentTypes(prev => ({ ...prev, representacion: representacionTypeElement.value }));
          }
        }

        // Actualizar los estados inmediatamente
        setOfertante(ofertanteActualizado);
        setContraparte(contraparteActualizada);
        setRepresentacion(representacionActualizada);

        return {
          ofertanteActualizado,
          contraparteActualizada,
          representacionActualizada,
        };
      };

      // Capturar valores actualizados
      const {
        ofertanteActualizado,
        contraparteActualizada,
        representacionActualizada,
      } = capturarValoresReales();

      // Actualizar datos del formulario
      updateFormData("ofertante", ofertanteActualizado);
      updateFormData("contraparte", contraparteActualizada);
      updateFormData("representacionContraparte", representacionActualizada);

      // Imprimir los datos para depuración
      console.log("Avanzando al paso 2 con datos capturados del DOM:", {
        ofertante: ofertanteActualizado,
        contraparte: contraparteActualizada,
        representacionContraparte: representacionActualizada,
      });

      // Crear objeto con los valores actualizados para enviar al componente padre
      const currentValues = {
        ofertante: ofertanteActualizado,
        contraparte: contraparteActualizada,
        representacionContraparte: representacionActualizada,
      };

      // Avanzar al siguiente paso (ahora incluye guardar)
      nextStep(currentValues);
    } else {
      setErrors(newErrors);
      console.log("Formulario inválido, no se puede avanzar", newErrors);
    }
  };

  const handleSaveDraft = () => {
    // Forzar actualización desde DOM antes de guardar
    const capturarValoresReales = () => {
      // Ofertante - usar función robusta para capturar valores
      const ofertanteActualizado = captureAllSectionValues("ofertante", ofertante);

      // Capturar tipo de documento del ofertante
      const ofertanteTypeElement = document.getElementById("ofertanteDocumentType");
      if (ofertanteTypeElement && ofertanteTypeElement.value) {
        setDocumentTypes(prev => ({ ...prev, ofertante: ofertanteTypeElement.value }));
      }

      // Contraparte - usar función robusta para capturar valores
      const contraparteActualizada = captureAllSectionValues("contraparte", contraparte);

      // Capturar tipo de documento de la contraparte
      const contraparteTypeElement = document.getElementById("contraparteDocumentType");
      if (contraparteTypeElement && contraparteTypeElement.value) {
        setDocumentTypes(prev => ({ ...prev, contraparte: contraparteTypeElement.value }));
      }

      // Representación - usar función robusta para capturar valores
      const representacionActualizada = { ...representacion };
      
      if (representacion.activa) {
        const representacionCaptured = captureAllSectionValues("representacion", representacion);
        Object.assign(representacionActualizada, representacionCaptured);

        // Capturar tipo de documento de la representación
        const representacionTypeElement = document.getElementById("representacionDocumentType");
        if (representacionTypeElement && representacionTypeElement.value) {
          setDocumentTypes(prev => ({ ...prev, representacion: representacionTypeElement.value }));
        }
      }

      // Actualizar los estados inmediatamente
      setOfertante(ofertanteActualizado);
      setContraparte(contraparteActualizada);
      setRepresentacion(representacionActualizada);

      return {
        ofertanteActualizado,
        contraparteActualizada,
        representacionActualizada,
      };
    };

    // Capturar valores actualizados
    const {
      ofertanteActualizado,
      contraparteActualizada,
      representacionActualizada,
    } = capturarValoresReales();

    // Log all input values before saving
    console.group("STEP 1 - VALUES WHEN SAVING");
    console.log("OFERTANTE:", {
      nombre: document.getElementById("ofertantenombre")?.value,
      apellidos: document.getElementById("ofertanteapellidos")?.value,
      email: document.getElementById("ofertanteemail")?.value,
      identificacion: document.getElementById("ofertanteIdentificacion")?.value,
      movil: document.getElementById("ofertantemovil")?.value,
      // Estado React actualizado
      estadoReact: ofertanteActualizado,
    });
    console.log("CONTRAPARTE:", {
      nombre: document.getElementById("contrapartenombre")?.value,
      apellidos: document.getElementById("contraparteapellidos")?.value,
      email: document.getElementById("contraparteemail")?.value,
      identificacion: document.getElementById("contraparteIdentificacion")
        ?.value,
      movil: document.getElementById("contrapartemovil")?.value,
      // Estado React actualizado
      estadoReact: contraparteActualizada,
    });
    if (representacion.activa) {
      console.log("REPRESENTACIÓN:", {
        nombre: document.getElementById("representacionnombre")?.value,
        apellidos: document.getElementById("representacionapellidos")?.value,
        email: document.getElementById("representacionemail")?.value,
        identificacion: document.getElementById("representacionIdentificacion")
          ?.value,
        movil: document.getElementById("representacionmovil")?.value,
        // Estado React actualizado
        estadoReact: representacionActualizada,
      });
    }
    console.groupEnd();

    // Actualizar datos del formulario antes de guardar
    updateFormData("ofertante", ofertanteActualizado);
    updateFormData("contraparte", contraparteActualizada);
    updateFormData("representacionContraparte", representacionActualizada);

    // Verify data before saving
    console.log("Saving draft with data:", {
      ofertante: ofertanteActualizado,
      contraparte: contraparteActualizada,
      representacion: representacionActualizada,
    });

    // Add a longer delay to ensure all state updates have propagated
    setTimeout(() => {
      // Guardar borrador pasando los valores actuales
      saveDraft({
        ofertante: ofertanteActualizado,
        contraparte: contraparteActualizada,
        representacionContraparte: representacionActualizada,
      });
    }, 200);
  };

  // Simplificar el detector de autocompletado para evitar conflictos
  useEffect(() => {
    // DESHABILITADO: El autofill está causando conflictos con los campos de identificación
    // Se ha deshabilitado temporalmente para permitir la escritura manual
    console.log("Autofill detection disabled to prevent conflicts with identification fields");
    
    // const autoFillListener = () => {
    //   const inputs = document.querySelectorAll(
    //     'input:not([type="checkbox"]):not([type="file"])'
    //   );
    //   let stateChanged = false;

    //   inputs.forEach((input) => {
    //     // Detectar autorellenado
    //     const isAutoFilled =
    //       getComputedStyle(input).getPropertyValue("background-color") !==
    //         "rgb(255, 255, 255)" || input.matches(":-webkit-autofill");

    //     if (isAutoFilled && input.value) {
    //       console.log(
    //         `Detected autofill on input: ${input.id || input.name} ${input.value}`
    //       );
    //       stateChanged = true;

    //       // Solo actualizar inputs básicos, no los que tienen manejo especial
    //       if (input.id === "referencia") {
    //         setReferencia(input.value);
    //         updateFormData("referencia", input.value);
    //       } else if (input.id === "objeto") {
    //         setObjeto(input.value);
    //         updateFormData("objeto", input.value);
    //       } else if (input.id === "cuantia") {
    //         setCuantia(input.value);
    //         updateFormData("cuantia", input.value);
    //       } else if (input.id === "plazoRespuesta") {
    //         try {
    //           // Ensure proper date format for the input field
    //           const dateValue = new Date(input.value);

    //           if (!isNaN(dateValue.getTime())) {
    //             // Format to YYYY-MM-DD for HTML date input
    //             const formattedDate = format(dateValue, "yyyy-MM-dd");
    //             setPlazoRespuesta(formattedDate);
    //             updateFormData("plazoRespuesta", formattedDate);
    //           }
    //         } catch (error) {
    //           console.error("Error formatting autofilled date:", error);
    //         }
    //       }
    //       // DESHABILITADO: No procesar campos de identificación con autofill para evitar conflictos
    //       else if (input.id && input.id.includes("identificacion")) {
    //         console.log("Skipping autofill for identification field to avoid conflicts");
    //       }
    //     }
    //   });

    //   if (stateChanged) {
    //     console.log("Autofill detected, updating form data");
    //     updateFormData("ofertante", ofertante);
    //     updateFormData("contraparte", contraparte);
    //     updateFormData("representacionContraparte", representacion);
    //   }
    // };

    // // Ejecutar el detector poco después de cargar y luego cada segundo
    // const timeoutId = setTimeout(autoFillListener, 2000);
    // const intervalId = setInterval(autoFillListener, 2000);

    // return () => {
    //   clearTimeout(timeoutId);
    //   clearInterval(intervalId);
    // };
    // We need to disable the exhaustive-deps rule here to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateFormData, allowAutoFill]);

  // Efecto para inicializar tipos de documento cuando se cargan datos existentes
  useEffect(() => {
    // Solo autoinferir tipo de documento en la carga inicial, nunca al escribir
    if (formData?.ofertante?.identificacion && !documentTypes.ofertante) {
      const detectDocumentType = (value) => {
        if (documentRegex.CIF.test(value)) return "CIF";
        if (documentRegex.NIF.test(value)) return "NIF";
        if (documentRegex.NIE.test(value)) return "NIE";
        if (documentRegex.Passport.test(value)) return "Passport";
        return "";
      };
      setDocumentTypes({
        ofertante: detectDocumentType(formData.ofertante.identificacion),
        contraparte: formData.contraparte?.identificacion ? detectDocumentType(formData.contraparte.identificacion) : "",
        representacion: formData.representacionContraparte?.identificacion ? detectDocumentType(formData.representacionContraparte.identificacion) : "",
      });
    }
    // No modificar documentTypes nunca más automáticamente
    // eslint-disable-next-line
  }, [formData]);

  // Añadir la función handleBack
  const handleBack = () => {
    navigate("/offers");
  };

  return (
    <FormContainer>
      {/* Contenedor horizontal para las dos primeras secciones */}
      <HorizontalSections>
        {/* Sección datos ofertante */}
        <FormSection isFirst>
          <FormWrapper>
            <SectionTitle>Datos de la parte ofertante</SectionTitle>
            <SectionDescription>
              Datos de la persona o empresa solicitante/demandante o de tu
              cliente, si eres su abogado o representante
            </SectionDescription>

            {renderField("ofertante", "nombre", "Nombre / Razón social (Empresas)")}
            {renderField("ofertante", "apellidos", "Apellidos / Forma societaria (SA, SL…)")}
            {renderDocumentField("ofertante", "DNI, NIE, NIF, Pasaporte")}
            {renderField("ofertante", "email", "Mail", "email")}
            {renderField("ofertante", "movil", "Móvil", "tel", false, true)}
            <RequiredMessage>*Campos obligatorios</RequiredMessage>
          </FormWrapper>
        </FormSection>

        {/* Sección datos contraparte y representación */}
        <div>
          {/* Sección datos contraparte */}
          <FormSection>
            <FormWrapper>
              <SectionTitle>Datos de la contraparte</SectionTitle>
              <SectionDescription>
                Datos de la persona o empresa receptora de la oferta vinculante
              </SectionDescription>

              {renderField("contraparte", "nombre", "Nombre / Razón social (Empresas)")}
              {renderField("contraparte", "apellidos", "Apellidos / Forma societaria (SA, SL…)")}
              {renderDocumentField("contraparte", "DNI, NIE, NIF, Pasaporte", false)}
              {renderField("contraparte", "email", "Mail", "email")}
              {renderField("contraparte", "movil", "Móvil", "tel", false, true)}
            </FormWrapper>
          </FormSection>

          {/* Sección representación de la contraparte */}
          <FormSection>
            <ToggleContainer>
              <SectionTitle style={{ minHeight: "auto" }}>
                Datos de la representación de la contraparte
              </SectionTitle>
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={representacion.activa}
                  onChange={toggleRepresentacion}
                  disabled={isReadOnly}
                />
                <ToggleSlider />
              </ToggleSwitch>
            </ToggleContainer>
            <SectionDescription style={{ minHeight: "auto" }}>
            Cuando rellenes los datos del Representante de la Contraparte, sólo le llegará
            a él la Oferta Vinculante, por lo que asegúrate de que se trata de los datos de la representación de la contraparte.
            </SectionDescription>

            {representacion.activa && (
              <div>
                {renderField("representacion", "nombre", "Nombre")}
                {renderField("representacion", "apellidos", "Apellidos")}
                {renderDocumentField("representacion", "DNI, NIE", false)}
                {renderField("representacion", "email", "Mail", "email")}
                {renderField(
                  "representacion",
                  "movil",
                  "Móvil",
                  "tel",
                  false,
                  true
                )}
              </div>
            )}
          </FormSection>
        </div>
      </HorizontalSections>

      {!isReadOnly && (
        <ButtonContainer>
          <ButtonRow>
            <SecondaryButton 
              onClick={handleBack}
              disabled={hasShownNetworkModal && !isOnline}
            >
              Atrás
            </SecondaryButton>
            <SaveButton
              onClick={handleSaveDraft}
              disabled={isSubmitting || isSavingDraft || (hasShownNetworkModal && !isOnline)}
            >
              {isSavingDraft ? "Guardando..." : "Guardar Borrador"}
            </SaveButton>
            <PrimaryButton
              onClick={handleContinue}
              disabled={!isFormValid || isSubmitting || isSavingDraft || (hasShownNetworkModal && !isOnline)}
            >
              {isSubmitting ? "Procesando..." : "Continuar"}
            </PrimaryButton>
          </ButtonRow>
          <StepIndicator>Paso 1 de 2</StepIndicator>
        </ButtonContainer>
      )}
    </FormContainer>
  );
};

export default OfertaStep1;
